import { generateUUID } from './utils/uuid';
/**
 * App — root component for RAAPID Pre-Visit Prospecting.
 *
 * Handles:
 * - SMART on FHIR launch (or mock/extension mode)
 * - Patient data display
 * - Suggestion list with filter/sort
 * - Accept/Reject workflow with FHIR write-back
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { useSession } from './context/SessionContext';
import { useSMARTLaunch } from './hooks/useSMARTLaunch';
import { useCodingSuggestions } from './hooks/useCodingSuggestions';
import { PatientBanner } from './components/PatientBanner';
import { RAFSummaryCard } from './components/RAFSummaryCard';
import { GapSummaryBar } from './components/GapSummaryBar';
import { SuggestionCard } from './components/SuggestionCard';
import { FilterBar } from './components/FilterBar';
import { SuggestionFormModal } from './components/SuggestionFormModal';
import { LoadingState } from './components/LoadingState';
import { ErrorState } from './components/ErrorState';
import { sendShutdownBeacon } from './services/fhirWriteback';
import { ConditionGroup } from './components/ConditionGroup';
import type { EHRTheme, FilterState, WorkflowMode, GapSummary, Encounter, ViewMode, CodingSuggestion } from './types';
import { isInScope } from './utils/specialtyMapping';

// ─── Theme helpers ────────────────────────────────────────────────────────────

function themeAccent(theme: EHRTheme) {
  if (theme === 'ecw') return 'text-ecw-teal border-ecw-teal';
  return 'text-cerner-blue border-cerner-blue';
}

function themeBrandBg(theme: EHRTheme) {
  if (theme === 'ecw') return 'bg-ecw-teal-light';
  return 'bg-cerner-blue-light';
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function App() {
  const { launch, isLoading: sessionLoading, error: sessionError, workflowMode, setWorkflowMode } = useSession();

  // Trigger SMART launch (no-op in mock/extension mode)
  useSMARTLaunch();

  const {
    data,
    isLoading: dataLoading,
    error: dataError,
    filters,
    setFilters,
    filteredSuggestions,
    actionLoading,
    acceptSuggestion,
    rejectSuggestion,
    editSuggestion,
    addSuggestion,
    resetSuggestionStatus,
    attestation,
    attestSession,
    conditionGroups,
  } = useCodingSuggestions();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [attestWarning, setAttestWarning] = useState<{ message: string; allowAttest: boolean } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('gapType');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const editingSuggestion = editingId ? data?.suggestions.find(s => s.id === editingId) ?? null : null;

  // Stable session ID — generated once per mount, not on every data change
  const sessionIdRef = useRef(generateUUID());

  // Graceful shutdown on chart close
  // data is accessed via ref so the effect only registers once
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  useEffect(() => {
    const handleUnload = () => {
      const acceptedIds = dataRef.current?.suggestions
        .filter((s) => s.status === 'accepted')
        .map((s) => s.id) ?? [];
      sendShutdownBeacon(sessionIdRef.current, acceptedIds);
    };
    window.addEventListener('pagehide', handleUnload);
    return () => window.removeEventListener('pagehide', handleUnload);
  }, []); // registers once — reads latest data via ref

  const isLoading = sessionLoading || dataLoading;
  const error = sessionError ?? dataError;

  // Stable callback — does not cause FilterBar to re-render on unrelated state changes
  const handleFilterChange = useCallback(
    (updated: Partial<FilterState>) => setFilters((prev) => ({ ...prev, ...updated })),
    [setFilters],
  );

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  if (!data) return null;

  const { patient, encounter, rafSummary, gapSummary, reviewPeriod } = data;
  const accentClass = themeAccent(launch.theme);
  const brandBgClass = themeBrandBg(launch.theme);

  // ┌─────────────────────────────────────────────────────────────────────────────┐
  // │ PRE-VISIT MODE — commented out, NOT deleted. Re-enable when pre-visit     │
  // │ prospecting workflow is ready for demo. DO NOT REMOVE these comments.     │
  // │                                                                           │
  // │ Pre-visit would prioritize suspect_new first, then opportunity, overlooked,│
  // │ compliance, recapture last. It also shows RAFSummaryCard instead of       │
  // │ PointOfCareHeader. Re-enable: uncomment WorkflowToggle in header JSX     │
  // │ and uncomment the RAF card branch in the render section below.            │
  // └─────────────────────────────────────────────────────────────────────────────┘

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden font-sans text-gray-800">

      {/* ── Patient Banner ─────────────────────────────────────────────── */}
      <PatientBanner patient={patient} encounter={encounter} />

      {/* ── Scrollable body ────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto">

        {/* Header + context card */}
        <div className={clsx('px-4 py-3 border-b border-gray-200', brandBgClass)}>
          {/* App header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div>
                <img
                  src={`${import.meta.env.BASE_URL}raapid-logo.png`}
                  alt="RAAPID"
                  style={{ height: '14px', display: 'block' }}
                  className={clsx(accentClass.includes('ecw') ? 'brightness-0 saturate-100 hue-rotate-[160deg]' : '')}
                />
                {/* PRE-VISIT: uncomment to restore dynamic subtitle. DO NOT REMOVE. */}
                <p className="text-2xs text-gray-500">
                  {/* {workflowMode === 'pre-visit' ? 'Pre-Visit Risk Adjustment' : 'Coding Intelligence'} */}
                  Coding Intelligence
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* PRE-VISIT: uncomment to restore workflow toggle. DO NOT REMOVE. */}
              {/* <WorkflowToggle mode={workflowMode} onChange={setWorkflowMode} /> */}
              <span className={clsx(
                'text-2xs px-2 py-0.5 rounded-full font-semibold border',
                launch.mode === 'mock'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : launch.mode === 'extension'
                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200',
              )}>
                {launch.mode === 'mock' ? 'Demo Mode' : launch.mode === 'extension' ? 'Extension' : 'Live'}
              </span>

              {/* View mode toggle */}
              <div className="flex items-center bg-gray-100 rounded-full p-0.5 border border-gray-200">
                <button
                  onClick={() => setViewMode('gapType')}
                  className={clsx(
                    'text-2xs font-semibold px-2 py-1 rounded-full transition-all duration-200',
                    viewMode === 'gapType' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  Gap Type
                </button>
                <button
                  onClick={() => setViewMode('condition')}
                  className={clsx(
                    'text-2xs font-semibold px-2 py-1 rounded-full transition-all duration-200',
                    viewMode === 'condition' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  By Condition
                </button>
              </div>

            </div>
          </div>

          {/* Attestation warning popup */}
          {attestWarning && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={() => setAttestWarning(null)}>
              <div className="bg-white rounded-lg shadow-xl w-full max-w-xs p-5" onClick={e => e.stopPropagation()}>
                <div className="flex items-start gap-3 mb-4">
                  <div className={clsx('flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center', attestWarning.allowAttest ? 'bg-blue-100' : 'bg-amber-100')}>
                    {attestWarning.allowAttest ? (
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                      <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-1">{attestWarning.allowAttest ? 'Confirm Attestation' : 'Review Required'}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{attestWarning.message}</p>
                  </div>
                </div>
                {attestWarning.allowAttest ? (
                  <div className="flex gap-2">
                    <button onClick={() => setAttestWarning(null)} className="flex-1 px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">Go Back</button>
                    <button onClick={() => { setAttestWarning(null); attestSession(launch.providerName ?? encounter.attendingPhysician ?? 'Provider'); }} className="flex-1 px-3 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors">Attest & Sign</button>
                  </div>
                ) : (
                  <button onClick={() => setAttestWarning(null)} className="w-full px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">OK</button>
                )}
              </div>
            </div>
          )}

          {/* Point-of-care: conditions-focused summary */}
          <PointOfCareHeader encounter={encounter} suggestions={data.suggestions} filteredSuggestions={filteredSuggestions} providerSpecialty={launch.providerSpecialty} />
          {/* PRE-VISIT: uncomment to restore RAF card toggle. DO NOT REMOVE.
          {workflowMode === 'pre-visit' ? (
            <RAFSummaryCard
              rafSummary={rafSummary}
              gapSummary={gapSummary}
              reviewPeriodStart={reviewPeriod.start}
              reviewPeriodEnd={reviewPeriod.end}
              showDollarEstimate={patient.riskAdjustmentEligible}
            />
          ) : (
            <PointOfCareHeader gapSummary={gapSummary} encounter={encounter} />
          )} */}
        </div>

        {/* Progress bar */}
        <GapSummaryBar gapSummary={{
          ...gapSummary,
          totalSuggestions: filteredSuggestions.length,
          accepted: filteredSuggestions.filter(s => s.status === 'accepted').length,
          rejected: filteredSuggestions.filter(s => s.status === 'rejected').length,
          pendingReview: filteredSuggestions.filter(s => s.status === 'pending').length,
        }} />

        {/* Filter bar */}
        <FilterBar
          filters={filters}
          onChange={handleFilterChange}
          totalCount={data.suggestions.length}
          filteredCount={filteredSuggestions.length}
          providerSpecialty={launch.providerSpecialty}
        />

        {/* ── Suggestion list ─────────────────────────────────────────── */}
        <div className="px-4 py-2 space-y-2">

          {/* ADD-NEW-CODE: DO NOT DELETE — commented out for point-of-care demo, re-enable when manual code entry is needed.
          <button
            onClick={() => setShowAddModal(true)}
            className={clsx(
              'w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed text-xs font-semibold transition-colors',
              launch.theme === 'ecw'
                ? 'border-ecw-teal/30 text-ecw-teal hover:bg-ecw-teal-light hover:border-ecw-teal/60'
                : 'border-cerner-blue/30 text-cerner-blue hover:bg-cerner-blue-light hover:border-cerner-blue/60',
            )}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Code
          </button>
          */}

          {filteredSuggestions.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm font-medium">No suggestions match your filters</p>
              <button
                onClick={() => setFilters((prev) => ({ ...prev, gapType: 'all', status: 'all', specialty: 'all', hccOnly: false, mySpecialtyOnly: false, searchQuery: '' }))}
                className="mt-2 text-xs text-blue-600 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* ── Condition View ──────────────────────────────────────── */}
          {viewMode === 'condition' && conditionGroups.length > 0 && (
            <div className="space-y-2">
              {conditionGroups.map((group) => (
                <ConditionGroup
                  key={group.hccCategory ?? group.suggestions[0]?.icdCode ?? group.problemListItems[0]?.icdCode}
                  group={group}
                  expandedSuggestionId={expandedIds}
                  onToggleExpand={(id) => toggleExpand(id)}
                  onAccept={(id) => acceptSuggestion(id)}
                  onReject={(id) => rejectSuggestion(id, 'Rejected')}
                  onEdit={(id) => setEditingId(id)}
                  onUndo={(id) => resetSuggestionStatus(id)}
                  actionLoading={actionLoading}
                />
              ))}
            </div>
          )}

          {/* ── Gap Type View ──────────────────────────────────────── */}
          {viewMode === 'gapType' && filteredSuggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              isExpanded={expandedIds.has(s.id)}
              onToggleExpand={() => toggleExpand(s.id)}
              onAccept={() => acceptSuggestion(s.id)}
              onReject={() => rejectSuggestion(s.id, 'Rejected')}
              onEdit={() => setEditingId(s.id)}
              onUndo={() => resetSuggestionStatus(s.id)}
              isLoading={actionLoading[s.id] ?? false}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 pb-6 mt-2 text-center">
          <p className="text-2xs text-gray-400">
            RAAPID v1.0 · Data as of{' '}
            {new Date(data.processedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* ── Sticky attestation footer ─────────────────────────────────── */}
      <AttestationFooter
        filteredSuggestions={filteredSuggestions}
        attestation={attestation}
        providerSpecialty={launch.providerSpecialty}
        providerName={launch.providerName ?? encounter.attendingPhysician ?? 'Provider'}
        onAttest={() => {
          const allSuggestions = data.suggestions;
          const inScopePending = allSuggestions.filter((s) => s.status === 'pending' && isInScope(s.hccCategory, launch.providerSpecialty)).length;
          const outOfScopePending = allSuggestions.filter((s) => s.status === 'pending' && !isInScope(s.hccCategory, launch.providerSpecialty)).length;
          const isSpecialist = launch.providerSpecialty && launch.providerSpecialty !== 'PCP';

          if (inScopePending > 0) {
            const scope = isSpecialist ? ` ${launch.providerSpecialty}` : '';
            setAttestWarning({ message: `Review ${inScopePending} remaining${scope} suggestion${inScopePending > 1 ? 's' : ''} before attesting.`, allowAttest: false });
          } else if (outOfScopePending > 0 && isSpecialist) {
            setAttestWarning({ message: `${launch.providerSpecialty} review complete. ${outOfScopePending} code${outOfScopePending > 1 ? 's' : ''} remain for other specialties.`, allowAttest: true });
          } else if (outOfScopePending > 0) {
            setAttestWarning({ message: `${outOfScopePending} suggestion${outOfScopePending > 1 ? 's' : ''} remaining. Continue to attest?`, allowAttest: true });
          } else {
            attestSession(launch.providerName ?? encounter.attendingPhysician ?? 'Provider');
          }
        }}
      />

      {/* Edit modal */}
      {editingSuggestion && (
        <SuggestionFormModal
          suggestion={editingSuggestion}
          onSave={(updated) => editSuggestion(editingSuggestion.id, updated)}
          onClose={() => setEditingId(null)}
        />
      )}

      {/* Add modal */}
      {showAddModal && (
        <SuggestionFormModal
          onSave={(newSuggestion) => addSuggestion(newSuggestion)}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

// ─── Point-of-care header (replaces RAF card) ─────────────────────────────────
// Physicians at point-of-care want to know: what to document, not RAF scores.

function PointOfCareHeader({ encounter, filteredSuggestions }: { encounter: Encounter; suggestions: CodingSuggestion[]; filteredSuggestions: CodingSuggestion[]; providerSpecialty: import('./types').ProviderSpecialty | null }) {
  const fSuspectNew = filteredSuggestions.filter(s => s.gapType === 'suspect_new').length;
  const fOpportunity = filteredSuggestions.filter(s => s.gapType === 'opportunity').length;
  const fRecapture = filteredSuggestions.filter(s => s.gapType === 'recapture').length;
  const fStaleRecapture = filteredSuggestions.filter(s => s.gapType === 'recapture_stale').length;
  const fOverClaimed = filteredSuggestions.filter(s => s.gapType === 'over_claimed').length;
  const fAccepted = filteredSuggestions.filter(s => s.status === 'accepted').length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      {/* Encounter context */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs font-semibold text-gray-800">{encounter.encounterType}</p>
          <p className="text-2xs text-gray-500 mt-0.5">
            {encounter.attendingPhysician} · {new Date(encounter.encounterDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Gap type chips — counts from filtered suggestions */}
      <div className="flex items-center gap-2 flex-wrap">
        {fSuspectNew > 0 && (
          <div className="flex items-center gap-1 text-2xs font-semibold rounded-full px-2.5 py-1 bg-suspect-badge text-suspect border border-suspect-border">
            <span className="text-sm font-bold leading-none">{fSuspectNew}</span> Suspecting Code Gap
          </div>
        )}
        {fOpportunity > 0 && (
          <div className="flex items-center gap-1 text-2xs font-semibold rounded-full px-2.5 py-1 bg-opportunity-badge text-opportunity border border-opportunity-border">
            <span className="text-sm font-bold leading-none">{fOpportunity}</span> Recaptured Opportunity
          </div>
        )}
        {fRecapture > 0 && (
          <div className="flex items-center gap-1 text-2xs font-semibold rounded-full px-2.5 py-1 bg-recapture-badge text-recapture border border-recapture-border">
            <span className="text-sm font-bold leading-none">{fRecapture}</span> Recaptured Existing
          </div>
        )}
        {fStaleRecapture > 0 && (
          <div className="flex items-center gap-1 text-2xs font-semibold rounded-full px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200">
            <span className="text-sm font-bold leading-none">{fStaleRecapture}</span> Suspecting Emerging
          </div>
        )}
        {fOverClaimed > 0 && (
          <div className="flex items-center gap-1 text-2xs font-semibold rounded-full px-2.5 py-1 bg-overclaimed-badge text-overclaimed border border-overclaimed-border">
            <span className="text-sm font-bold leading-none">{fOverClaimed}</span> Recaptured Risk
          </div>
        )}
        {fAccepted > 0 && (
          <div className="flex items-center gap-1 text-2xs font-semibold rounded-full px-2.5 py-1 bg-accepted-bg text-accepted border border-accepted-border">
            <span className="text-sm font-bold leading-none">{fAccepted}</span> Accepted
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Workflow mode toggle ────────────────────────────────────────────────────

function WorkflowToggle({ mode, onChange }: { mode: WorkflowMode; onChange: (m: WorkflowMode) => void }) {
  return (
    <div className="flex items-center bg-gray-100 rounded-full p-0.5 border border-gray-200">
      <button
        onClick={() => onChange('pre-visit')}
        className={clsx(
          'text-2xs font-semibold px-2.5 py-1 rounded-full transition-all duration-200',
          mode === 'pre-visit'
            ? 'bg-white text-gray-800 shadow-sm'
            : 'text-gray-500 hover:text-gray-700',
        )}
        aria-pressed={mode === 'pre-visit'}
        title="Pre-visit prospecting — suspect codes prioritized"
      >
        Pre-Visit
      </button>
      <button
        onClick={() => onChange('point-of-care')}
        className={clsx(
          'text-2xs font-semibold px-2.5 py-1 rounded-full transition-all duration-200',
          mode === 'point-of-care'
            ? 'bg-white text-gray-800 shadow-sm'
            : 'text-gray-500 hover:text-gray-700',
        )}
        aria-pressed={mode === 'point-of-care'}
        title="Point of care — recaptured codes prioritized for quick action"
      >
        Point of Care
      </button>
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

// ─── Sticky attestation footer ──────────────────────────────────────────────

function AttestationFooter({ filteredSuggestions, attestation, providerSpecialty, providerName, onAttest }: {
  filteredSuggestions: CodingSuggestion[];
  attestation: { attestedAt: string; attestedBy: string } | null;
  providerSpecialty: import('./types').ProviderSpecialty | null;
  providerName: string;
  onAttest: () => void;
}) {
  const reviewedCount = filteredSuggestions.filter((s) => s.status !== 'pending').length;
  const total = filteredSuggestions.length;
  const pct = total > 0 ? Math.round((reviewedCount / total) * 100) : 0;
  const allReviewed = reviewedCount === total && total > 0;

  if (attestation) {
    return (
      <div className="flex-shrink-0 px-4 py-2 bg-emerald-50 border-t-2 border-emerald-300 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
          <span className="text-xs font-semibold text-emerald-700">
            Attested{providerSpecialty && providerSpecialty !== 'PCP' ? ` (${providerSpecialty})` : ''}
          </span>
        </div>
        <span className="text-2xs text-emerald-600">
          {providerName} · {new Date(attestation.attestedAt).toLocaleTimeString()}
        </span>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 px-4 py-2 bg-white border-t-2 border-gray-200 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-2xs font-semibold text-gray-600">{reviewedCount} of {total} reviewed</span>
          <span className="text-2xs text-gray-400">{pct}%</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={clsx('h-full rounded-full transition-all duration-500', allReviewed ? 'bg-emerald-500' : 'bg-blue-400')} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <button
        onClick={onAttest}
        disabled={total === 0}
        className={clsx(
          'flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold transition-all flex-shrink-0',
          allReviewed
            ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200',
        )}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        Attest & Sign
      </button>
    </div>
  );
}

